import { WATER_NUTRIENTS } from '../lib/pralTransfer'
import { pralItemsFromEntries, pralSigOf, sumPral } from '../kidney/pral'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import i18n from '@/lib/i18n'
import { useAuth } from '@/auth/AuthProvider'
import { DiaryService } from '@/lib/firestore/diary'
import {
  diarySearchCacheClear,
  diarySearchCachePatch,
  diarySearchCacheRemove,
} from '@/diary/diarySearch'
import {
  writeDailySummary,
  writeDailyTargets,
  createDailyTargetsIfAbsent,
  fetchDaySummaryState,
  type DaySummaryDocState,
} from '@/lib/firestore/summaries'
import { updateStreak, checkStreakStatus } from '@/lib/firestore/streak'
import { computeSummary, trackedTotalsOf } from '@/diary/summary'
import {
  resolveDailyTargets,
  effectiveTargetsForDate,
  backfillTargetsForDate,
  needsBackfill,
  targetsEqual,
  targetsHash,
  type DayTargets,
  type TargetInput,
  type WeightPoint,
} from '@/kidney/dailyTargets'
import { classifyDay, classifyStoredDay, type DayPerformance } from '@/kidney/dayPerformance'
import { useWeightHistory } from '@/lib/weightHistoryCache'
import type { DaySummaryDoc } from '@/lib/firestore/progress'
import { todayStr, addDays } from '@/lib/dates'
import { copySourceLocked } from '../../../shared/mealCopy'
import { isDayKeyLocked } from '@/diary/historyLock'
import type {
  DiaryEntry,
  DiaryEntryInput,
  DailySummary,
  FluidType,
  MealTime,
} from '@/types'

interface DiaryContextValue {
  selectedDate: string
  entries: DiaryEntry[]
  summary: DailySummary | null
  loading: boolean
  setSelectedDate: (d: string) => void
  goToToday: () => void
  goToPreviousDay: () => void
  goToNextDay: () => void
  canGoToNextDay: boolean
  addEntry: (input: DiaryEntryInput) => Promise<string | null>
  addFluid: (ml: number, type?: FluidType) => Promise<string | null>
  updateEntry: (id: string, partial: Partial<DiaryEntry>) => Promise<void>
  deleteEntry: (id: string) => Promise<void>
  copyDay: (source: string, target: string, includeFluids?: boolean) => Promise<number>
  copyEntry: (entry: DiaryEntry, targetDate: string, targetMealType?: MealTime) => Promise<string>
  copyEntries: (entriesToCopy: DiaryEntry[], targetDate: string, targetMealType?: MealTime) => Promise<number>
  getEntriesByMealType: (m: MealTime) => DiaryEntry[]
  getFluidEntries: () => DiaryEntry[]
  // One-time "streak freeze used" notice — a streak-update call just bridged
  // a missed day instead of resetting. Diary shows a small inline banner.
  freezeNotice: boolean
  dismissFreezeNotice: () => void

  // Per-day targets (4.34). Past days are judged by the targets that applied
  // on that day (`dailySummaries.targets`); today by the live profile.
  today: string                                    // local 'YYYY-MM-DD', refreshed at midnight / focus
  targetInput: TargetInput
  liveTargets: DayTargets | null                   // today's resolved targets
  dayTargets: DayTargets | null                    // effective targets for selectedDate
  weightHistory: WeightPoint[] | null              // null while loading
  dayDocs: Record<string, DaySummaryDocState>      // retained day docs, keyed by date
  loadDayDocs: (dates: string[], force?: boolean) => void
  targetsForDate: (date: string) => DayTargets | null
  performanceForDate: (date: string) => DayPerformance
}

const DiaryContext = createContext<DiaryContextValue | null>(null)

export function DiaryProvider({ children }: { children: ReactNode }) {
  const { user, userProfile } = useAuth()
  const [selectedDate, setSelectedDate] = useState<string>(todayStr())
  const [entries, setEntries] = useState<DiaryEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [subFailed, setSubFailed] = useState(false)
  const [freezeNotice, setFreezeNotice] = useState(false)

  // Midnight rollover: selectedDate is seeded once at mount, so a tab left
  // open over midnight stays stuck on yesterday and new logs land on the
  // wrong date. On tab focus, advance to the new "today" — but only when the
  // user was already viewing the old "today"; a deliberately selected past
  // date stays put.
  const todayRef = useRef(todayStr())
  const [today, setToday] = useState(todayRef.current)
  const rollToday = useCallback(() => {
    const newToday = todayStr()
    if (newToday === todayRef.current) return
    setSelectedDate((current) => (current === todayRef.current ? newToday : current))
    todayRef.current = newToday
    setToday(newToday)
  }, [])
  useEffect(() => {
    const rollover = () => {
      if (document.visibilityState !== 'visible') return
      rollToday()
    }
    document.addEventListener('visibilitychange', rollover)
    window.addEventListener('focus', rollover)
    return () => {
      document.removeEventListener('visibilitychange', rollover)
      window.removeEventListener('focus', rollover)
    }
  }, [rollToday])
  // A tab that simply stays open crosses midnight with no focus event; the
  // per-day targets writer (W1) must snapshot the new day, so arm a timer for
  // the next local midnight and re-arm after each fire (mobile parity).
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    const arm = () => {
      const now = new Date()
      const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 1)
      timer = setTimeout(() => {
        rollToday()
        arm()
      }, Math.max(1000, next.getTime() - now.getTime()))
    }
    arm()
    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [rollToday, today])
  // And re-check on every date navigation.
  useEffect(() => {
    rollToday()
  }, [selectedDate, rollToday])

  // ---- Per-day targets (4.34) ----------------------------------------------
  const targetInput = useMemo<TargetInput>(
    () => ({ kidneyProfile: userProfile?.kidneyProfile ?? null, isPremium: userProfile?.isPremium === true }),
    [userProfile?.kidneyProfile, userProfile?.isPremium],
  )
  const liveTargets = useMemo(() => resolveDailyTargets(targetInput), [targetInput])
  const liveHash = targetsHash(liveTargets)
  const weightHistory = useWeightHistory(user?.uid)

  // Retained day docs (date → state). Readers derive colours from these at
  // render time; W2/W3 patch them after writing so nothing needs a refetch.
  const [dayDocs, setDayDocs] = useState<Record<string, DaySummaryDocState>>({})
  const dayDocsRef = useRef<Record<string, DaySummaryDocState>>({})
  const dayDocsUidRef = useRef<string | null>(null)
  const dayDocsEpochRef = useRef(0)
  const inflightDocs = useRef(new Map<string, Partial<DaySummaryDoc>>())
  useEffect(() => {
    const uid = user?.uid ?? null
    if (dayDocsUidRef.current === uid) return
    dayDocsUidRef.current = uid
    dayDocsEpochRef.current += 1
    dayDocsRef.current = {}
    inflightDocs.current = new Map()
    setDayDocs({})
  }, [user?.uid])
  const setDayDoc = useCallback((date: string, state: DaySummaryDocState) => {
    dayDocsRef.current = { ...dayDocsRef.current, [date]: state }
    setDayDocs(dayDocsRef.current)
  }, [])
  const patchDayDoc = useCallback(
    (date: string, patch: Partial<DaySummaryDoc>) => {
      const pendingPatch = inflightDocs.current.get(date)
      if (pendingPatch) Object.assign(pendingPatch, patch)
      const cur = dayDocsRef.current[date]
      const base: DaySummaryDoc =
        cur?.status === 'loaded'
          ? cur.doc
          : { date, totalItems: 0, sodium: 0, potassium: 0, phosphorus: 0, protein: 0, fluid: 0, performance: 'none' }
      setDayDoc(date, { status: 'loaded', doc: { ...base, ...patch } })
    },
    [setDayDoc],
  )
  const loadDayDocs = useCallback(
    (dates: string[], force = false) => {
      const uid = user?.uid
      if (!uid) return
      for (const date of dates) {
        const known = dayDocsRef.current[date]
        if (!force && known && known.status !== 'error') continue
        if (inflightDocs.current.has(date)) continue
        const pending = inflightDocs.current
        const epoch = dayDocsEpochRef.current
        const patches: Partial<DaySummaryDoc> = {}
        pending.set(date, patches)
        fetchDaySummaryState(uid, date)
          .then((state) => {
            if (dayDocsEpochRef.current !== epoch || dayDocsUidRef.current !== uid) return
            if (state.status === 'loaded') {
              setDayDoc(date, { status: 'loaded', doc: { ...state.doc, ...patches } })
            } else if (Object.keys(patches).length === 0) {
              setDayDoc(date, state)
            }
          })
          .finally(() => pending.delete(date))
      }
    },
    [user?.uid, setDayDoc],
  )

  useEffect(() => {
    if (user?.uid) loadDayDocs([selectedDate, today])
  }, [user?.uid, selectedDate, today, loadDayDocs])

  const selectedDocState = dayDocsUidRef.current === user?.uid ? dayDocs[selectedDate] : undefined
  const selectedDoc = selectedDocState?.status === 'loaded' ? selectedDocState.doc : null

  // Effective targets for the selected day (D4).
  const dayTargets = useMemo(() => {
    if (selectedDate >= today) return liveTargets
    return effectiveTargetsForDate(selectedDate, selectedDoc, targetInput, today)
  }, [selectedDate, today, liveTargets, selectedDoc, targetInput, weightHistory])

  // Readiness for anything that PERSISTS (W2, W3): the doc state is known and
  // — for a past day without a snapshot — the weight history has loaded. A
  // read error is never treated as absence.
  const targetsReady = useMemo(() => {
    if (selectedDate >= today) return true
    if (!selectedDocState || selectedDocState.status === 'error') return false
    if (!needsBackfill(selectedDate, selectedDoc, today)) return true
    return weightHistory !== null
  }, [selectedDate, today, selectedDocState, selectedDoc, weightHistory])

  // For DISPLAY: hold the summary while a past day's doc is still loading so
  // the rings never flash today's limits; a read error falls back to the
  // estimate so a transient failure cannot blank the diary (writes stay off).
  const targetsDisplayReady =
    selectedDate >= today ||
    (!!selectedDocState && (selectedDocState.status === 'error' || targetsReady))

  const targetsForDate = useCallback(
    (date: string): DayTargets | null => {
      const t = todayRef.current
      if (date >= t) return liveTargets
      const st = dayDocsRef.current[date]
      const doc = st?.status === 'loaded' ? st.doc : null
      return effectiveTargetsForDate(date, doc, targetInput, t)
      // dayDocs/today are listed so callers re-render when retained docs change
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [liveTargets, targetInput, weightHistory, dayDocs, today],
  )

  const performanceForDate = useCallback(
    (date: string): DayPerformance => {
      const st = dayDocs[date]
      if (!st || st.status !== 'loaded') return 'none'
      return classifyStoredDay(st.doc, targetsForDate(date))
    },
    [dayDocs, targetsForDate],
  )

  // Fire-and-forget streak update that also surfaces the one-time freeze
  // notice. Kept non-blocking (same as before) so callers don't wait on it.
  const updateStreakAndNotify = useCallback((uid: string, entryDate?: string) => {
    updateStreak(uid, entryDate)
      .then((result) => {
        if (result.freezeConsumed) setFreezeNotice(true)
      })
      .catch(() => {})
  }, [])

  // Real-time entries for the selected day.
  useEffect(() => {
    if (!user) {
      setEntries([])
      // Never leave one account's logged foods searchable after sign-out.
      diarySearchCacheClear()
      return
    }
    // Clear immediately so a date switch never shows (or persists) the prior day's data.
    setEntries([])
    setLoading(true)
    setSubFailed(false)
    const unsub = DiaryService.subscribeToEntriesForDate(
      user.uid,
      selectedDate,
      (next) => {
        setEntries(next)
        setSubFailed(false)
        setLoading(false)
      },
      () => {
        // On subscription error, entries is still the reset [] — mark the day
        // failed so the summary writer never clobbers the doc with zeros.
        setSubFailed(true)
        setLoading(false)
      },
    )
    return unsub
  }, [user, selectedDate])

  // Per-day targets (4.34): the day's effective targets, not today's profile.
  // Held back until the day's targets are known so a past day never renders
  // against today's limits.
  const summary = useMemo(
    () => (targetsDisplayReady ? computeSummary(selectedDate, entries, dayTargets) : null),
    [selectedDate, entries, dayTargets, targetsDisplayReady],
  )

  /**
   * W2 — debounced persist of the day's rollup (week-strip dots + mobile
   * parity). 4.34: `performance` is classified against the day's EFFECTIVE
   * targets (so opening a past day no longer rewrites its dot with today's
   * limits), the write carries every tracked total (`totals`, stamped
   * `totalsAt`), it waits for the day's targets to be known (`targetsReady`),
   * and never touches `targets` — only W1/W3 write those.
   */
  const writeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastW2 = useRef<string | null>(null)
  useEffect(() => {
    if (!user || !summary || !targetsReady) return
    // Don't persist while the day's snapshot is still loading (or errored) —
    // `entries` may be empty/stale, which would clobber the shared
    // dailySummaries doc with bad totals.
    if (loading || subFailed) return
    // Never materialize summaries for future days (mobile can't navigate
    // there and would never create them).
    if (selectedDate > todayStr()) return
    const uid = user.uid
    const date = selectedDate
    const epoch = dayDocsEpochRef.current
    const totalItems = entries.length
    const totals = trackedTotalsOf(summary.totals)
    const perf = classifyDay({ totalItems, totals, targets: dayTargets })
    // Patching the retained doc re-creates it, and a past day whose targets are
    // still an in-memory estimate then produces a fresh (equal) DayTargets — so
    // without this the effect would re-fire forever on an unchanged day. Any
    // real change to totals / count / colour changes the key and still writes.
    // 4.35 PRAL: sum over known items + coverage. In the key, so a coverage-only change
    // (a certificate invalidated, same totals) is still written.
    const pral = sumPral(pralItemsFromEntries(entries))
    const writeKey = `${uid}|${date}|${totalItems}|${perf}|${JSON.stringify(totals)}|${JSON.stringify(pral)}`
    if (lastW2.current === writeKey) return
    if (writeTimer.current) clearTimeout(writeTimer.current)
    writeTimer.current = setTimeout(() => {
      lastW2.current = writeKey
      writeDailySummary(uid, summary, perf, totalItems, pral)
        .then(() => {
          if (dayDocsEpochRef.current !== epoch || dayDocsUidRef.current !== uid) return
          // Mirror the write into the retained doc so render-time colours
          // update without a refetch. The server stamps are unknown here; an
          // equal local pair keeps `totals` trusted until the next read.
          const stamp = { seconds: Math.floor(Date.now() / 1000), nanoseconds: 0 }
          patchDayDoc(date, {
            totalItems,
            sodium: Math.round(summary.totals.sodiumMg),
            potassium: Math.round(summary.totals.potassiumMg),
            phosphorus: Math.round(summary.totals.phosphorusMg),
            protein: Math.round(summary.totals.proteinG),
            fluid: Math.round(summary.totals.fluidMl),
            performance: perf,
            totals,
            totalsAt: stamp,
            pral,
            pralAt: stamp,
            updatedAt: stamp,
          })
        })
        .catch((err) => {
          // A failed write must be retried when the same values come round again.
          if (lastW2.current === writeKey) lastW2.current = null
          console.warn('Failed to update daily summary:', err)
        })
    }, 600)
    return () => {
      if (writeTimer.current) clearTimeout(writeTimer.current)
    }
  }, [user, summary, entries, loading, subFailed, selectedDate, targetsReady, dayTargets, patchDayDoc])

  /**
   * W1 — snapshot TODAY's resolved targets onto today's summary doc.
   *
   * Independent of `selectedDate`: a Settings limit edit, a weight log, a
   * stage or comorbidity change all reach here through the realtime profile
   * and rewrite today's snapshot immediately, whatever day the diary shows.
   * Keyed by uid + date; the hash is recorded only after a successful write;
   * the local date is re-read after the async read so a run that crosses
   * midnight is abandoned; a run token invalidates in-flight work.
   */
  const w1Done = useRef<{ key: string; hash: string } | null>(null)
  useEffect(() => {
    const uid = user?.uid
    if (!uid || !userProfile || !liveTargets) return
    const key = `${uid}/${today}`
    if (w1Done.current?.key === key && w1Done.current.hash === liveHash) return

    let cancelled = false
    let attempt = 0
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    const retry = () => {
      if (cancelled || attempt >= 3) return
      attempt += 1
      retryTimer = setTimeout(() => void run(), 1000 * 2 ** attempt)
    }
    const run = async () => {
      const state = await fetchDaySummaryState(uid, today)
      if (cancelled) return
      if (todayStr() !== today) return // crossed midnight during the read
      if (state.status === 'error') {
        retry()
        return
      }
      const stored = state.status === 'loaded' ? state.doc.targets ?? null : null
      if (stored && targetsEqual(stored, liveTargets)) {
        w1Done.current = { key, hash: liveHash }
        patchDayDoc(today, { targets: stored })
        return
      }
      try {
        await writeDailyTargets(uid, today, liveTargets)
        if (cancelled) return
        w1Done.current = { key, hash: liveHash }
        patchDayDoc(today, { targets: liveTargets })
      } catch (err) {
        console.warn("Failed to snapshot today's targets:", err)
        retry()
      }
    }
    void run()
    return () => {
      cancelled = true
      if (retryTimer) clearTimeout(retryTimer)
    }
    // liveHash stands in for liveTargets (same identity semantics, stable string)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `subFailed` only re-runs the effect after a transient failure; W1 never
    // reads entries, so it is not a guard.
  }, [user?.uid, !!userProfile, today, liveHash, subFailed])

  /**
   * W3 — backfill a PAST day that has no snapshot, once, inside a transaction
   * that re-checks absence. Skipped on a read error (unknown ≠ absent) and
   * until the weight history has loaded.
   */
  useEffect(() => {
    const uid = user?.uid
    if (!uid || selectedDate >= today) return
    if (!selectedDocState || selectedDocState.status === 'error') return
    if (!needsBackfill(selectedDate, selectedDoc, today)) return
    if (!weightHistory) return
    const targets = backfillTargetsForDate(targetInput)
    if (!targets) return

    let cancelled = false
    const date = selectedDate
    createDailyTargetsIfAbsent(uid, date, targets)
      .then((result) => {
        if (cancelled) return
        if (result === 'created') patchDayDoc(date, { targets })
        else loadDayDocs([date], true) // another device got there first — read theirs
      })
      .catch((err) => console.warn('Failed to backfill day targets:', err))
    return () => {
      cancelled = true
    }
  }, [user?.uid, selectedDate, today, selectedDocState, selectedDoc, weightHistory, targetInput, patchDayDoc, loadDayDocs])

  // On load, zero a stale streak (mobile checkStreakStatus parity).
  useEffect(() => {
    if (!user) return
    void checkStreakStatus(user.uid).catch(() => {})
  }, [user])

  const addEntry = useCallback(
    async (input: DiaryEntryInput): Promise<string | null> => {
      if (!user) return null
      const entryDate = input.date || selectedDate
      const id = await DiaryService.createEntry(user.uid, {
        ...input,
        date: entryDate,
      })
      // Mobile updates the streak after every diary entry — keep them in lockstep.
      updateStreakAndNotify(user.uid, entryDate)
      return id
    },
    [user, selectedDate, updateStreakAndNotify],
  )

  const addFluid = useCallback(
    (ml: number, type: FluidType = 'water') =>
      addEntry({
        date: selectedDate,
        mealType: 'snack',
        name: i18n.t('diary.fluids.water', 'Water'),
        source: 'fluid',
        servingSize: ml,
        servingUnit: 'ml',
        servingsConsumed: 1,
        // 4.35 PRAL: plain water is certified at creation (five known zeros); any other
        // fluid type logged through this zero-nutrient shortcut is NOT.
        ...(type === 'water'
          ? { nutrients: { ...WATER_NUTRIENTS }, pralSig: pralSigOf(WATER_NUTRIENTS) as string }
          : { nutrients: { sodiumMg: 0, potassiumMg: 0, phosphorusMg: 0, proteinG: 0 } }),
        isFluid: true,
        fluidMl: ml,
        fluidType: type,
        safetyLevel: 'safe',
      }),
    [addEntry, selectedDate],
  )

  // 4.37 F2: every entry copy passes through here, so a day outside the free
  // history window can't be copied from whichever button started it.
  const assertCopySourceOpen = (list: readonly DiaryEntry[]) => {
    const isPremium = userProfile?.isPremium === true
    if (copySourceLocked(list, (d) => isDayKeyLocked(d, isPremium, today))) {
      throw new Error('copy-source-locked')
    }
  }

  const value: DiaryContextValue = {
    selectedDate,
    entries,
    summary,
    loading,
    setSelectedDate,
    goToToday: () => setSelectedDate(todayStr()),
    goToPreviousDay: () => setSelectedDate((d) => addDays(d, -1)),
    // Future days are navigable (meal planning) — mobile parity.
    goToNextDay: () => setSelectedDate((d) => addDays(d, 1)),
    canGoToNextDay: true,
    addEntry,
    addFluid,
    updateEntry: async (id, partial) => {
      await DiaryService.updateEntry(id, partial)
      // Diary search only fetches forward of its cursor, so an edit is
      // invisible to it unless we patch the cached copy here.
      diarySearchCachePatch(id, partial)
    },
    deleteEntry: async (id) => {
      await DiaryService.deleteEntry(id)
      // Same reason: drop it from the search window or it comes back as a phantom.
      diarySearchCacheRemove(id)
    },
    copyDay: async (source, target, includeFluids) => {
      if (!user) return 0
      const count = await DiaryService.copyEntriesForDate(user.uid, source, target, includeFluids)
      // Mobile updates the streak when a copy actually logged something.
      if (count > 0) updateStreakAndNotify(user.uid, target)
      return count
    },
    copyEntry: async (entry, targetDate, targetMealType) => {
      assertCopySourceOpen([entry])
      const id = await DiaryService.copySingleEntry(entry, targetDate, targetMealType)
      if (user) updateStreakAndNotify(user.uid, targetDate)
      return id
    },
    copyEntries: async (entriesToCopy, targetDate, targetMealType) => {
      assertCopySourceOpen(entriesToCopy)
      const n = await DiaryService.copyMultipleEntries(entriesToCopy, targetDate, targetMealType)
      if (user && n > 0) updateStreakAndNotify(user.uid, targetDate)
      return n
    },
    getEntriesByMealType: (m) =>
      entries.filter(
        (e) =>
          e.mealType === m &&
          // Keep nutritive entries; hide pure-water/fluid logs (they live in the fluid tracker).
          !(
            e.isFluid &&
            !e.nutrients.sodiumMg &&
            !e.nutrients.potassiumMg &&
            !e.nutrients.phosphorusMg &&
            !e.nutrients.proteinG
          ),
      ),
    getFluidEntries: () => entries.filter((e) => e.isFluid),
    freezeNotice,
    dismissFreezeNotice: () => setFreezeNotice(false),
    // Per-day targets (4.34)
    today,
    targetInput,
    liveTargets,
    dayTargets,
    weightHistory,
    dayDocs,
    loadDayDocs,
    targetsForDate,
    performanceForDate,
  }

  return <DiaryContext.Provider value={value}>{children}</DiaryContext.Provider>
}

export function useDiary(): DiaryContextValue {
  const ctx = useContext(DiaryContext)
  if (!ctx) throw new Error('useDiary must be used within DiaryProvider')
  return ctx
}
